import Button from "@/common/components/Button"
import { CurrencyTextField } from "@/common/components/CurrencyTextField"
import MaskedTextField from "@/common/components/MaskedTextField"
import SelectController from "@/common/components/SelectController"
import { useGeosOptions } from "@/common/hooks/queries/useGeosOptions"
import { useIndustrySegments } from "@/common/hooks/queries/useIndustrySegments"
import { useProductCategories } from "@/common/hooks/queries/useProductCategories"
import useDebounce from "@/common/hooks/useDebounce"
import { GeoTransformers } from "@/common/services/geoTransformers";
import { Mapper } from "@/common/services/mapper"
import { IndustrySegmentData } from "@/common/types/industry_segments"
import { formatCurrency } from "@/common/utils/formatters"
import { RequestedReview } from "@/features/establishmentLeadsFunnel/components/RequestedReview"
import { ReviewButtons } from "@/features/establishmentLeadsFunnel/components/ReviewButtons"
import { PrivateCompanyLeadsFunnelUpdateValidationSchema } from "@/features/privateCompanyLeadsFunnel/schemas/update.schema"
import { ContractData, FinancialInformationData, OperationalInformationData } from "@/features/privateCompanyLeadsFunnel/types/privateCompanyLead"
import { ProductCategoryData } from "@/features/productCategories/types/productCategories"
import { filterOptions } from "@/features/users/constants/filterOptions.constant"
import { UseMutateFunction } from "@tanstack/react-query"
import { AxiosResponse } from "axios"
import { FilterOptionOption } from "node_modules/react-select/dist/declarations/src/filters"
import { BaseSyntheticEvent } from "react"
import { Control, Controller, FieldErrors, UseFormRegister } from "react-hook-form"
import { useNavigate } from "react-router-dom"
import CompanyCreditAlert from "./CompanyCreditAlert"

interface OperationalInformationSectionProps {
  privateCompanyLeadId?: string
  operational_information?: OperationalInformationData
  financial_information?: FinancialInformationData
  contract?: ContractData
  control: Control<PrivateCompanyLeadsFunnelUpdateValidationSchema, any>
  register: UseFormRegister<PrivateCompanyLeadsFunnelUpdateValidationSchema>
  errors: FieldErrors<PrivateCompanyLeadsFunnelUpdateValidationSchema>
  submit?: (e?: BaseSyntheticEvent) => Promise<void>
  submittableOnBlur?: (e: React.FocusEvent<HTMLInputElement, Element>, onBlur: (e: React.FocusEvent<HTMLInputElement, Element>) => void) => void
  approveOperationalInformationLead?: UseMutateFunction<void, Error, { description?: string } | undefined, unknown>
  declineOperationalInformationLead?: UseMutateFunction<void, Error, { description?: string } | undefined, unknown>
  createPrivateCompanyLeadContract?: UseMutateFunction<AxiosResponse<any, any>, Error, void, unknown>
  isToAttachContract?: boolean
  disabled?: boolean
}

export const OperationalInformationSection = ({
  privateCompanyLeadId,
  operational_information,
  financial_information,
  contract,
  control,
  register,
  errors,
  submit,
  submittableOnBlur,
  declineOperationalInformationLead,
  approveOperationalInformationLead,
  createPrivateCompanyLeadContract,
  isToAttachContract,
  disabled,
}: OperationalInformationSectionProps) => {
  const [productCategoriesSearch, setProductCategoriesSearch] = useDebounce('');
  const [industrySegmentSearch, setIndustrySegmentSearch] = useDebounce('');
  const [geoSearch, setGeoSearch] = useDebounce('');
  const geos = useGeosOptions(geoSearch)

  const { data: productCategoryOptions } = useProductCategories({
    mapper: Mapper.mapToOptions<ProductCategoryData>({ labelFieldName: 'display_name', valueFieldName: 'id' }),
    filters: { search: productCategoriesSearch },
  })

  const { data: industrySegmentOptions } = useIndustrySegments({
    mapper: Mapper.mapToOptions<IndustrySegmentData>({ labelFieldName: 'display_name', valueFieldName: 'id' }),
    filters: { search: industrySegmentSearch },
  })

  const navigate = useNavigate()

  return (
    <div className="flex flex-col gap-4">
      <div className="flex justify-between">
        <div className="flex w-full justify-between">
          <p className="text-[18px] font-bold">Dados Operacionais</p>
          {privateCompanyLeadId && (
            <>
              {createPrivateCompanyLeadContract ? (
                <Button
                  type="button"
                  name="private_company_contract_details"
                  variant='yellow'
                  onClick={() => contract ? navigate(`/private-clients/${privateCompanyLeadId}/contracts/${contract?.id}/edit`) : createPrivateCompanyLeadContract()}
                >
                  Gerar condição comercial
                </Button>
              ) : (
                <Button
                  type="button"
                  name="private_company_contract_details"
                  variant={isToAttachContract ? 'yellow' : 'default'}
                  onClick={() => navigate(`/private-clients/${privateCompanyLeadId}/contracts/${contract?.id}/edit`)}
                >
                  {isToAttachContract ? 'Anexar Contrato' : 'Ver condição comercial'}
                </Button>
              )}
            </>
          )}
        </div>
        {(approveOperationalInformationLead && declineOperationalInformationLead) && (
          <ReviewButtons
            reviewState={operational_information?.last_review?.state}
            onApprove={approveOperationalInformationLead}
            onDecline={declineOperationalInformationLead}
          />
        )}
      </div>
      {operational_information?.last_review?.state === 'disapproved' && (
        <RequestedReview description={operational_information?.last_review?.description} />
      )}
      <div className="flex gap-2">
        <Controller
          name="operational_information_attributes.expected_credit"
          control={control}
          defaultValue={operational_information?.expected_credit ? formatCurrency(operational_information?.expected_credit) : undefined}
          disabled={disabled || !!financial_information}
          render={({ field }) => (
            <div className="w-1/2 flex flex-col gap-0.5">
              <CurrencyTextField
                label="Expectativa de crédito"
                markAsRequired
                allowZero
                maxValue={999_999_999.99}
                {...field}
                onBlur={() => submit?.()}
                errorMessage={errors?.operational_information_attributes?.expected_credit?.message}
              />

              {financial_information?.approved_credit && (
                <p className="italic text-xs text-white-400" data-testid="operational_information_attributes_approved_credit_info">
                  Crédito aprovado: {formatCurrency(financial_information?.approved_credit)}
                </p>
              )}
            </div>
          )}
        />

        <SelectController
          control={control}
          name='operational_information_attributes.industry_segment'
          label="Segmento de atuação"
          className="w-1/2"
          onInputChange={setIndustrySegmentSearch}
          triggerOnChange={submit}
          errorMessage={errors?.operational_information_attributes?.industry_segment?.message}
          defaultValue={{ label: operational_information?.industry_segment?.display_name, value: operational_information?.industry_segment?.id }}
          options={industrySegmentOptions || []}
          isDisabled={disabled}
          markAsRequired
        />
      </div>

      {financial_information?.approved_credit && Number(financial_information?.approved_credit) === 0 && (
        <CompanyCreditAlert />
      )}

      <SelectController
        label="Expectativa de Serviços"
        isMulti
        control={control}
        triggerOnChange={submit}
        onInputChange={setProductCategoriesSearch}
        defaultValue={operational_information?.product_categories?.map((product) => ({ label: product.display_name, value: product.id }))}
        name='operational_information_attributes.product_categories'
        options={productCategoryOptions || []}
        isDisabled={disabled}
        markAsRequired
      />
      <div className="flex gap-2">
        <MaskedTextField
          mask='000000'
          label="Quantidade total de Equipamentos"
          className="w-1/3"
          errorMessage={errors?.operational_information_attributes?.expected_equipment_quantity?.message}
          defaultValue={operational_information?.expected_equipment_quantity}
          {...register('operational_information_attributes.expected_equipment_quantity')}
          onBlur={(e) => submittableOnBlur && submittableOnBlur(e, register('operational_information_attributes.expected_equipment_quantity').onBlur)}
          disabled={disabled}
          markAsRequired
        />
        <SelectController
          control={control}
          name='operational_information_attributes.geos'
          label="Região de atuação desejada"
          className="w-2/3"
          triggerOnChange={submit}
          isMulti
          onInputChange={setGeoSearch}
          defaultValue={GeoTransformers.mapToGeo()(operational_information?.geo_geoables)}
          filterOption={filterOptions as unknown as (option: FilterOptionOption<unknown>, inputValue: string) => boolean}
          options={GeoTransformers.mapToGeoOptions()(geos)}
          isDisabled={disabled}
          markAsRequired
        />
      </div>
    </div>
  )
}
